import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { createOrganizationVenue } from '../api/venues'
import { ApiError } from '../api/client'
import { billingErrorMessage, parseBillingError } from '../api/apiErrorCodes'
import type { BuyerVenueDto, OrganizationDto } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { PaywallModal } from '../components/billing/PaywallModal'
import { AddVenueModal } from '../components/venues/AddVenueModal'
import type { VenueFormValues } from '../utils/venueFormValidation'
import { clearActiveVenueId, loadActiveVenueId, saveActiveVenueId } from './venueStorage'

type PaywallState = {
  title: string
  message: string
}

type ActiveVenueContextValue = {
  activeVenueId: number | null
  activeVenueName: string | null
  venues: BuyerVenueDto[]
  venuesUsed: number
  venuesLimit: number
  canManageVenues: boolean
  switchVenue: (venueId: number) => void
  requestAddVenue: () => void
  refreshVenues: () => Promise<void>
}

const ActiveVenueContext = createContext<ActiveVenueContextValue | null>(null)

const resolveVenues = (org: OrganizationDto | undefined) => org?.venues ?? []

export const ActiveVenueProvider = ({ children }: { children: ReactNode }) => {
  const { session, role, organizations, refreshOrganizations } = useAuth()
  const organizationId = session?.organizationId ?? null
  const currentOrg = organizations.find((org) => org.id === organizationId)
  const [activeVenueId, setActiveVenueId] = useState<number | null>(null)
  const [paywall, setPaywall] = useState<PaywallState | null>(null)
  const [addVenueOpen, setAddVenueOpen] = useState(false)
  const [addVenueSubmitting, setAddVenueSubmitting] = useState(false)
  const [addVenueError, setAddVenueError] = useState<string | null>(null)

  const venues = resolveVenues(currentOrg)

  useEffect(() => {
    if (role !== 'buyer' || organizationId == null) {
      setActiveVenueId(null)
      return
    }
    const stored = loadActiveVenueId(organizationId)
    const fallback = venues[0]?.id ?? null
    const next =
      stored != null && venues.some((venue) => venue.id === stored) ? stored : fallback
    setActiveVenueId(next)
    if (next != null) {
      saveActiveVenueId(organizationId, next)
    }
  }, [organizationId, role, venues])

  const switchVenue = useCallback(
    (venueId: number) => {
      if (organizationId == null) {
        return
      }
      setActiveVenueId(venueId)
      saveActiveVenueId(organizationId, venueId)
    },
    [organizationId],
  )

  const refreshVenues = useCallback(async () => {
    await refreshOrganizations()
  }, [refreshOrganizations])

  const requestAddVenue = useCallback(() => {
    if (organizationId == null || currentOrg?.can_edit !== true) {
      setPaywall({
        title: 'Только владельцу',
        message: 'Добавлять заведения может только владелец организации.',
      })
      return
    }

    setAddVenueError(null)
    setAddVenueOpen(true)
  }, [currentOrg?.can_edit, organizationId])

  const handleAddVenueSubmit = useCallback(
    async (values: VenueFormValues) => {
      if (organizationId == null) {
        return
      }

      setAddVenueSubmitting(true)
      setAddVenueError(null)

      try {
        const created = await createOrganizationVenue(organizationId, {
          legal_name: values.legalName,
          trade_name: values.tradeName,
          ogrn: values.ogrn,
          city: values.city,
          horeca_type: values.horecaType,
        })
        await refreshOrganizations()
        switchVenue(created.id)
        setAddVenueOpen(false)
      } catch (error) {
        const billing = parseBillingError(error)
        if (billing?.errorCode === 'venue_limit') {
          setAddVenueOpen(false)
          setPaywall({
            title: 'Несколько заведений',
            message: billing.message,
          })
          return
        }
        if (billing?.errorCode === 'venue_addon_required') {
          setAddVenueOpen(false)
          setPaywall({
            title: 'Дополнительное заведение',
            message: billing.message,
          })
          return
        }
        const message =
          error instanceof ApiError ? billingErrorMessage(error) : 'Не удалось создать заведение'
        setAddVenueError(message)
      } finally {
        setAddVenueSubmitting(false)
      }
    },
    [organizationId, refreshOrganizations, switchVenue],
  )

  const activeVenueName = useMemo(() => {
    const venue = venues.find((item) => item.id === activeVenueId)
    return venue?.name ?? null
  }, [activeVenueId, venues])

  const value = useMemo<ActiveVenueContextValue>(
    () => ({
      activeVenueId,
      activeVenueName,
      venues,
      venuesUsed: currentOrg?.venues_used ?? venues.length,
      venuesLimit: currentOrg?.venues_limit ?? Math.max(venues.length, 1),
      canManageVenues: currentOrg?.can_edit === true,
      switchVenue,
      requestAddVenue,
      refreshVenues,
    }),
    [
      activeVenueId,
      activeVenueName,
      venues,
      currentOrg?.venues_used,
      currentOrg?.venues_limit,
      currentOrg?.can_edit,
      switchVenue,
      requestAddVenue,
      refreshVenues,
    ],
  )

  useEffect(() => {
    if (organizationId != null && role !== 'buyer') {
      clearActiveVenueId(organizationId)
    }
  }, [organizationId, role])

  return (
    <ActiveVenueContext.Provider value={value}>
      {children}
      <AddVenueModal
        open={addVenueOpen}
        defaultCity={currentOrg?.city}
        defaultHorecaType={currentOrg?.horeca_type}
        submitting={addVenueSubmitting}
        formError={addVenueError}
        onDismiss={() => {
          if (!addVenueSubmitting) {
            setAddVenueOpen(false)
            setAddVenueError(null)
          }
        }}
        onSubmit={handleAddVenueSubmit}
      />
      <PaywallModal
        open={paywall != null}
        title={paywall?.title ?? ''}
        message={paywall?.message ?? ''}
        onDismiss={() => setPaywall(null)}
      />
    </ActiveVenueContext.Provider>
  )
}

export const useActiveVenue = () => {
  const context = useContext(ActiveVenueContext)
  if (!context) {
    throw new Error('useActiveVenue must be used within ActiveVenueProvider')
  }
  return context
}
